import {Button, createMuiTheme, ThemeProvider} from "@material-ui/core";
import {App} from "./app";
import {store} from "./redux/store";
import {Provider} from "react-redux";
import React, {createContext, useRef, useState} from "react";
import {SnackbarProvider} from "notistack";
import Zoom from "@material-ui/core/Zoom";
import { useTranslation } from "react-i18next";
import { Viewer } from "./viewer/js/main";
import { TaskQueueProvider } from "./task-queue";

// NOTE: Initial import is required to use react-i18next
import './localization';

const themeSpacing = 8;

declare module '@material-ui/core/styles/createPalette' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface Palette {
    custom?: Palette['primary'];
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface PaletteOptions {
    custom?: PaletteOptions['primary'];
  }
}

const lightenDarkenColor = (col, amt) => {
  let usePound = false;

  if (col[0] === "#") {
    col = col.slice(1);
    usePound = true;
  }

  let num = parseInt(col,16);

  let r = (num >> 16) + amt;
  if (r > 255) r = 255;
  else if  (r < 0) r = 0;

  let b = ((num >> 8) & 0x00FF) + amt;
  if (b > 255) b = 255;
  else if  (b < 0) b = 0;

  let g = (num & 0x0000FF) + amt;
  if (g > 255) {
    g = 255;
  } else if (g < 0) {
    g = 0;
  }

  return (usePound?"#":"") + String("000000" + (g | (b << 8) | (r << 16)).toString(16)).slice(-6);
};

const theme = createMuiTheme({
  spacing: themeSpacing,
  zIndex: {
    appBar: 1100,
    modal: 1250,
    drawer: 1200,
    snackbar: 1400,
    tooltip: 1500
  },
  overrides: {
    MuiPopover: {
      root: {
        "z-index": "1300 !important"
      }
    },
    MuiTooltip: {
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.5)",
      },
      arrow: {
        "&::before": {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          border: "0px"
        }
      },
    },
    MuiDialogTitle: {
      root: {
        padding: `${themeSpacing}px`,
        '& h2': {
          fontSize: "1.0rem !important"
        }
      }
    },
    MuiDialogContent: {
      root: {
        padding: `${2*themeSpacing}px`,
      }
    },
    MuiIconButton: {
      root: {
        padding: `${themeSpacing}px`
      }
    }
  },
  palette: {
    primary: {
      main: '#162031',
      light: lightenDarkenColor('#162031', 25),
      dark: lightenDarkenColor('#162031', -25)
    },
    secondary: {
      main: '#dd0c29',
      light: lightenDarkenColor('#dd0c29', 25),
      dark: lightenDarkenColor('#dd0c29', -25)
    },
    custom: {
      main: '#5c6ac4',
      light: lightenDarkenColor('#5c6ac4', 25),
      dark: lightenDarkenColor('#5c6ac4', -25)
    },
  },
  mixins: {
    toolbar: {
      // override default
    }
  }
});

const MessageProvider = (props) => {
  const {t} = useTranslation();
  const messageRef = useRef(null);

  const onMessageClose = key => () => {
    messageRef.current.closeSnackbar(key);
  };

  return (
    <SnackbarProvider
      ref={messageRef}
      maxSnack={2}
      TransitionComponent={Zoom}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      action={(key) => (
        <Button size="medium" onClick={onMessageClose(key)}>
          {t('buttons.dismiss')}
        </Button>
      )}
    >
      {props.children}
    </SnackbarProvider>
  );
};

export const ViewerContext = createContext({
  viewer: null as Viewer,
  setViewer: (value: any) => {}
});

const ViewerProvider = (props) => {
  const [viewer, setViewer] = useState(null);

  return (
    <ViewerContext.Provider value={{viewer, setViewer}}>
      {props.children}
    </ViewerContext.Provider>
  );
};

const EngineViewer = () => {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <ViewerProvider>
          <MessageProvider>
            <TaskQueueProvider>
              <App/>
            </TaskQueueProvider>
          </MessageProvider>
        </ViewerProvider>
      </ThemeProvider>
    </Provider>
  );
};

export default EngineViewer;